/**
 * IOPGPS (https://www.iopgps.com) adapter.
 *
 * Supports IOPGPS Open API (WanWay / IOPGPS REST API) as well as fallback
 * GPSWOX / Traccar integrations for IOPGPS white-label servers.
 *
 * Returns standard normalized device array:
 *   { success: true, data: [ { id, name, uniqueId, status, position: { latitude, longitude, speed, course, altitude, address, fixTime }, raw } ] }
 */

import { traccarFetchAll } from './traccar';

const DEFAULT_URL = 'https://www.iopgps.com';

const SERVER_PROXY = (() => {
  if (typeof window !== 'undefined') {
    const { protocol, host } = window.location;
    if (!protocol.startsWith('file')) return `${protocol}//${host}/proxy/`;
  }
  return null;
})();

const TIMEOUT = 30000;

async function iopgpsFetch(absoluteUrl, { method = 'GET', token, body } = {}) {
  const url = SERVER_PROXY ? `${SERVER_PROXY}${absoluteUrl}` : absoluteUrl;
  const controller = new AbortController();
  const to = setTimeout(() => controller.abort(), TIMEOUT);
  try {
    const headers = { Accept: 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      headers.AccessToken = token;
    }
    const opts = { method, headers, signal: controller.signal };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    const res = await fetch(url, opts);
    const text = await res.text();
    let json = null;
    try { json = text && (text.trim().startsWith('{') || text.trim().startsWith('[')) ? JSON.parse(text) : null; } catch (_) {}
    return { ok: res.ok, status: res.status, text, json };
  } finally {
    clearTimeout(to);
  }
}

const pick = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
};
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : undefined; };

function normalizeDevice(v) {
  const lat = num(pick(v, ['lat', 'latitude', 'Latitude', 'last_lat', 'y']));
  const lng = num(pick(v, ['lng', 'lon', 'long', 'longitude', 'Longitude', 'last_lng', 'x']));
  const speed = num(pick(v, ['speed', 'velocity', 'spd'])) || 0;
  const course = num(pick(v, ['course', 'heading', 'direction', 'bearing', 'angle'])) || 0;
  const time = pick(v, ['time', 'timestamp', 'fixTime', 'deviceTime', 'updated_at', 'lastTime', 'gps_time', 'positionTime']);
  const id = pick(v, ['id', 'device_id', 'deviceId', 'imei', 'uniqueId', 'unique_id', 'targetId', '_id']);
  const name = pick(v, ['name', 'targetName', 'plate', 'matricule', 'vehicle_name', 'deviceName']) || `Device ${id}`;
  const imei = pick(v, ['imei', 'unique_id', 'uniqueId', 'serial', 'targetId']);
  const onlineRaw = pick(v, ['online', 'status', 'state', 'isOnline', 'deviceStatus']);
  const offline = String(onlineRaw).toLowerCase() === 'offline' || onlineRaw === false || onlineRaw === 0;

  return {
    id,
    name,
    uniqueId: String(imei || id),
    status: offline ? 'offline' : 'online',
    lastUpdate: time,
    odometer: num(pick(v, ['odometer', 'mileage', 'totalDistance'])) ?? null,
    sensors: [],
    raw: v,
    position: (lat !== undefined && lng !== undefined) ? {
      latitude: lat,
      longitude: lng,
      speed: speed || 0,
      course: course || 0,
      altitude: num(pick(v, ['altitude', 'alt', 'z'])) || 0,
      address: pick(v, ['address', 'location', 'cityName']) || '',
      fixTime: time,
      attributes: {},
    } : null,
  };
}

export async function iopgpsFetchAll(settings = {}) {
  const url = (settings.iopgpsUrl || settings.traccarUrl || DEFAULT_URL).replace(/\/+$/, '');
  const email = (settings.iopgpsEmail || settings.traccarEmail || '').trim();
  const password = settings.iopgpsPassword || settings.traccarPassword || '';

  if (!email || !password) {
    return { success: false, error: 'IOPGPS: identifiant (email/compte) et mot de passe requis', data: [] };
  }

  // 1. Try IOPGPS Open API endpoints first
  const loginPaths = [
    '/api/authorization',
    '/api/login',
    '/api/auth/login',
    '/api/v1/auth/login',
    '/api/user/login',
  ];
  const loginPayloads = [
    { account: email, secretKey: password },
    { account: email, password },
    { user_name: email, password },
    { username: email, password },
    { email, password },
  ];

  let token = null;
  let accountId = null;

  for (const path of loginPaths) {
    for (const body of loginPayloads) {
      try {
        const res = await iopgpsFetch(`${url}${path}`, { method: 'POST', body });
        if (res.ok && res.json) {
          const t = pick(res.json, ['AccessToken', 'token', 'access_token', 'jwt']);
          if (t) {
            token = t;
            accountId = pick(res.json, ['AccountId', 'accountId', 'userId', 'id']);
            break;
          }
          if (res.json.data && typeof res.json.data === 'object') {
            const innerToken = pick(res.json.data, ['AccessToken', 'token', 'access_token']);
            if (innerToken) {
              token = innerToken;
              accountId = pick(res.json.data, ['AccountId', 'accountId', 'userId']);
              break;
            }
          }
        }
      } catch (_) {}
    }
    if (token) break;
  }

  // 2. If token acquired, fetch vehicle location list
  if (token) {
    const vehiclePaths = [
      `/api/vehicle/location${accountId ? `?AccountId=${accountId}` : ''}`,
      '/api/vehicle/list',
      '/api/devices',
      '/api/vehicles',
      '/api/positions',
      '/api/unit/list',
    ];

    for (const path of vehiclePaths) {
      try {
        const res = await iopgpsFetch(`${url}${path}`, { token });
        if (res.ok && res.json) {
          let list = null;
          if (Array.isArray(res.json)) list = res.json;
          else if (Array.isArray(res.json.data)) list = res.json.data;
          else if (Array.isArray(res.json.records)) list = res.json.records;
          else if (Array.isArray(res.json.vehicles)) list = res.json.vehicles;
          else if (res.json.data && Array.isArray(res.json.data.vehicles)) list = res.json.data.vehicles;

          if (list && list.length > 0) {
            return { success: true, data: list.map(normalizeDevice).filter(d => d.id) };
          }
        }
      } catch (_) {}
    }
  }

  // 3. Fallback: Delegate to the GPSWOX/Traccar provider with IOPGPS credentials
  return traccarFetchAll({
    traccarEnabled: true,
    traccarUrl: url,
    traccarEmail: email,
    traccarPassword: password,
    traccarProvider: 'iopgps',
    traccarRefreshSec: settings.traccarRefreshSec,
    corsProxy: settings.corsProxy,
  });
}
